import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, ExternalLink, ArrowRight, Sparkles, ClipboardList } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { NotifyCTA } from "@/components/NotifyCTA";
import { ZoomableImage } from "@/components/ZoomableImage";
import { PromoSignupModal } from "@/components/PromoSignupModal";
import { RichText } from "@/components/RichText";

const isInternal = (link) => typeof link === "string" && link.startsWith("/");

function CtaIcon({ link }) {
  if (link?.startsWith("tel:")) return <Phone className="h-4 w-4" />;
  if (link?.startsWith("mailto:")) return <Mail className="h-4 w-4" />;
  if (isInternal(link)) return <ArrowRight className="h-4 w-4" />;
  return <ExternalLink className="h-4 w-4" />;
}

export default function Promos() {
  const [site, setSite] = useState(null);
  const [signupPromo, setSignupPromo] = useState(null);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  const promos = (site?.promos || []).filter((p) => p.active !== false);

  return (
    <Layout>
      <Seo title="Billiards News, Clinics & Events in Quinte | Quinte BCA" description="Billiards news, clinics, giveaways and upcoming events with the Quinte BCA Pool League in Trenton and the Quinte region of Ontario." path="/promos" />
      <PageHeader
        eyebrow="Don't miss out"
        title="News/Promos"
        subtitle={site?.promos_intro || "Special clinics, events and giveaways happening with the league."}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {!site ? (
          <p className="text-[#555555]">Loading promos…</p>
        ) : promos.length === 0 ? (
          <p className="text-[#555555]">No promos or giveaways right now — check back soon!</p>
        ) : (
          <div className="space-y-10" data-testid="promos-list">
            {promos.map((p, i) => (
              <div
                key={p.id}
                data-testid={`promo-card-${p.id}`}
                className="grid gap-6 md:grid-cols-2 items-center bg-white rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden"
              >
                {p.image && (
                  <div className={`bg-[#0B0B0C] ${i % 2 === 1 ? "md:order-2" : ""}`}>
                    <ZoomableImage
                      src={fileUrl(p.image)}
                      alt={p.title}
                      testId={`promo-image-${p.id}`}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                )}
                <div className="p-6 md:p-8">
                  {p.badge && (
                    <span className="inline-flex items-center gap-1.5 bg-[#E11D2A]/10 text-[#E11D2A] rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide mb-3">
                      <Sparkles className="h-3.5 w-3.5" /> {p.badge}
                    </span>
                  )}
                  <h2 className="font-heading font-extrabold text-2xl leading-tight">{p.title}</h2>
                  {p.description && <RichText html={p.description} className="text-sm mt-3" testid={`promo-desc-view-${p.id || ""}`} />}
                  {p.cta_type === "form" ? (
                    p.cta_label && (
                      <button
                        onClick={() => setSignupPromo(p)}
                        data-testid={`promo-cta-${p.id}`}
                        className="inline-flex items-center gap-2 mt-5 bg-[#1E63C4] text-white rounded-md px-5 py-2.5 font-semibold hover:bg-[#174e9c] transition-colors"
                      >
                        <ClipboardList className="h-4 w-4" /> {p.cta_label}
                      </button>
                    )
                  ) : (
                    p.cta_label && p.cta_link && (
                      isInternal(p.cta_link) ? (
                        <Link
                          to={p.cta_link}
                          data-testid={`promo-cta-${p.id}`}
                          className="inline-flex items-center gap-2 mt-5 bg-[#1E63C4] text-white rounded-md px-5 py-2.5 font-semibold hover:bg-[#174e9c] transition-colors"
                        >
                          <CtaIcon link={p.cta_link} /> {p.cta_label}
                        </Link>
                      ) : (
                        <a
                          href={p.cta_link}
                          target={p.cta_link.startsWith("http") ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          data-testid={`promo-cta-${p.id}`}
                          className="inline-flex items-center gap-2 mt-5 bg-[#1E63C4] text-white rounded-md px-5 py-2.5 font-semibold hover:bg-[#174e9c] transition-colors"
                        >
                          <CtaIcon link={p.cta_link} /> {p.cta_label}
                        </a>
                      )
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {signupPromo && <PromoSignupModal promo={signupPromo} onClose={() => setSignupPromo(null)} />}
      <NotifyCTA title="Get notified about new promos & giveaways" text="Don't miss a draw or special offer — we'll email you the moment a new promo drops. Pick what you want to hear about." />
    </Layout>
  );
}
